/**
 * Error Handling Utilities for Form Builder
 * 
 * Provides consistent error handling and user-friendly messages
 * for form builder operations.
 */

import { notify } from '@/utils/notify';

/**
 * Error types for form builder operations
 */
export const ErrorTypes = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  FILE_UPLOAD_ERROR: 'FILE_UPLOAD_ERROR',
  FILE_DOWNLOAD_ERROR: 'FILE_DOWNLOAD_ERROR',
  FORM_NOT_FOUND: 'FORM_NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

/**
 * Map HTTP status codes to error types
 */
const STATUS_TO_ERROR_TYPE = {
  400: ErrorTypes.VALIDATION_ERROR,
  401: ErrorTypes.UNAUTHORIZED,
  403: ErrorTypes.UNAUTHORIZED,
  404: ErrorTypes.FORM_NOT_FOUND,
  500: ErrorTypes.SERVER_ERROR,
  502: ErrorTypes.NETWORK_ERROR,
  503: ErrorTypes.NETWORK_ERROR,
  504: ErrorTypes.NETWORK_ERROR
};

/**
 * User-friendly error messages
 */
const ERROR_MESSAGES = {
  [ErrorTypes.NETWORK_ERROR]: 'Network connection failed. Please check your internet connection and try again.',
  [ErrorTypes.VALIDATION_ERROR]: 'Invalid form data. Please check your inputs and try again.',
  [ErrorTypes.FILE_UPLOAD_ERROR]: 'Failed to upload file. Please try again.',
  [ErrorTypes.FILE_DOWNLOAD_ERROR]: 'Failed to load image. Using placeholder.',
  [ErrorTypes.FORM_NOT_FOUND]: 'Form not found. It may have been deleted.',
  [ErrorTypes.UNAUTHORIZED]: 'You are not authorized to perform this action.',
  [ErrorTypes.SERVER_ERROR]: 'Server error occurred. Please try again later.',
  [ErrorTypes.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.'
};

/**
 * Parse error response and determine error type
 * 
 * @param {Error} error - Error object from API call
 * @returns {Object} - { type, message, originalError }
 */
export function parseError(error) {
  // Network error (no response)
  if (!error.response) {
    return {
      type: ErrorTypes.NETWORK_ERROR,
      message: ERROR_MESSAGES[ErrorTypes.NETWORK_ERROR],
      originalError: error
    };
  }

  // HTTP error with response
  const status = error.response?.status;
  const serverMessage = error.response?.data?.message || error.message;
  
  const errorType = STATUS_TO_ERROR_TYPE[status] || ErrorTypes.UNKNOWN_ERROR;
  
  return {
    type: errorType,
    message: serverMessage || ERROR_MESSAGES[errorType],
    originalError: error,
    statusCode: status
  };
}

/**
 * Handle error and show notification to user
 * 
 * @param {Error} error - Error object
 * @param {String} context - Context of the operation (e.g., 'saving form', 'loading forms')
 * @param {Object} options - { silent: boolean, customMessage: string }
 * @returns {Object} - Parsed error object
 */
export function handleError(error, context = '', options = {}) {
  const { silent = false, customMessage } = options;
  
  const parsedError = parseError(error);
  
  // Log to console for debugging
  console.error(`[FormBuilder Error] ${context}:`, {
    type: parsedError.type,
    message: parsedError.message,
    statusCode: parsedError.statusCode,
    originalError: parsedError.originalError
  });

  // Show notification unless silent
  if (!silent) {
    const notificationMessage = customMessage || 
      (context ? `Error ${context}: ${parsedError.message}` : parsedError.message);
    
    notify({
      type: 'error',
      message: notificationMessage
    });
  }

  return parsedError;
}

/**
 * Handle form save error with specific messaging
 * 
 * @param {Error} error - Error object
 * @param {Boolean} isUpdate - Whether this was an update (vs create)
 */
export function handleFormSaveError(error, isUpdate = false) {
  const context = isUpdate ? 'updating form' : 'creating form';
  const parsedError = parseError(error);
  
  let message;
  switch (parsedError.type) {
    case ErrorTypes.VALIDATION_ERROR:
      message = 'Please check that all required fields are filled correctly.';
      break;
    case ErrorTypes.FILE_UPLOAD_ERROR:
      message = 'Failed to upload images/logos. Please try again or use smaller files.';
      break;
    case ErrorTypes.UNAUTHORIZED:
      message = 'You do not have permission to save forms.';
      break;
    default:
      message = parsedError.message;
  }
  
  console.error(`[FormBuilder Error] ${context}:`, parsedError);
  
  notify({
    type: 'error',
    message: `Failed to ${isUpdate ? 'update' : 'create'} form: ${message}`
  });
  
  return parsedError;
}

/**
 * Handle form load error with specific messaging
 * 
 * @param {Error} error - Error object
 * @param {String} formId - Form ID that failed to load
 */
export function handleFormLoadError(error, formId) {
  const parsedError = parseError(error);
  
  let message;
  switch (parsedError.type) {
    case ErrorTypes.FORM_NOT_FOUND:
      message = 'This form may have been deleted or you may not have access to it.';
      break;
    case ErrorTypes.UNAUTHORIZED:
      message = 'You do not have permission to view this form.';
      break;
    case ErrorTypes.FILE_DOWNLOAD_ERROR:
      message = 'Form loaded but some images failed to load. Placeholders will be shown.';
      break;
    default:
      message = parsedError.message;
  }
  
  console.error('[FormBuilder Error] loading form:', { formId, error: parsedError });
  
  notify({
    type: 'error',
    message: `Failed to load form: ${message}`
  });
  
  return parsedError;
}

/**
 * Handle file operation errors (upload/download)
 * 
 * @param {Error} error - Error object
 * @param {String} operation - 'upload' or 'download'
 * @param {String} fileType - 'image' or 'logo'
 * @param {Object} options - { silent: boolean }
 */
export function handleFileError(error, operation, fileType, options = {}) {
  const { silent = false } = options;
  
  const errorType = operation === 'upload' 
    ? ErrorTypes.FILE_UPLOAD_ERROR 
    : ErrorTypes.FILE_DOWNLOAD_ERROR;
  
  const parsedError = {
    type: errorType,
    message: ERROR_MESSAGES[errorType],
    originalError: error
  };
  
  console.error(`[FormBuilder Error] ${operation} ${fileType}:`, parsedError);
  
  if (!silent) {
    // For downloads, show warning instead of error
    const notificationType = operation === 'download' ? 'warning' : 'error';
    
    notify({
      type: notificationType,
      message: `Failed to ${operation} ${fileType}. ${
        operation === 'download' 
          ? 'Using placeholder image.' 
          : 'Please try again with a smaller file.'
      }`
    });
  }
  
  return parsedError;
}

/**
 * Validate form data before save
 * Throws validation error if invalid
 * 
 * @param {Object} formData - Form data to validate
 * @throws {Error} Validation error
 */
export function validateFormData(formData) {
  const errors = [];
  
  // Check required fields
  if (!formData.name || formData.name.trim().length === 0) {
    errors.push('Form name is required');
  }
  
  if (formData.name && formData.name.length > 255) {
    errors.push('Form name must be less than 255 characters');
  }
  
  if (!formData.definition || !formData.definition.fields) {
    errors.push('Form must have at least one field');
  }
  
  if (formData.definition?.fields?.length === 0) {
    errors.push('Form must have at least one field');
  }
  
  // Check for duplicate IDs (should never happen, but safety check)
  if (formData.definition?.fields) {
    const ids = formData.definition.fields
      .map(field => field.id)
      .filter(Boolean);
    
    const duplicates = ids.filter((id, index) => ids.indexOf(id) !== index);
    
    if (duplicates.length > 0) {
      errors.push(`Duplicate field IDs detected: ${duplicates.join(', ')}`);
    }
  }
  
  if (errors.length > 0) {
    const error = new Error(errors.join('; '));
    error.type = ErrorTypes.VALIDATION_ERROR;
    error.validationErrors = errors;
    throw error;
  }
  
  return true;
}

/**
 * Retry a function with exponential backoff
 * Useful for network requests that may temporarily fail
 * 
 * @param {Function} fn - Async function to retry
 * @param {Number} maxRetries - Maximum number of retries
 * @param {Number} baseDelay - Base delay in ms (will be exponentially increased)
 * @returns {Promise} - Result of function
 */
export async function retryWithBackoff(fn, maxRetries = 3, baseDelay = 1000) {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Don't retry on validation or authorization errors
      const parsedError = parseError(error);
      if (
        parsedError.type === ErrorTypes.VALIDATION_ERROR ||
        parsedError.type === ErrorTypes.UNAUTHORIZED ||
        parsedError.type === ErrorTypes.FORM_NOT_FOUND
      ) {
        throw error;
      }
      
      // If not last attempt, wait before retrying
      if (attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt);
        console.warn(`[FormBuilder] Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // All retries failed
  throw lastError;
}
